// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, AppFooter, useCurrentUser } from "./common";
import { usePermissionsFromSession } from "../components/permissions";
import { Modal, Field } from "../components/Modal";

// Report screen — 리포트
// Region role: scheduled/ad-hoc reports + KPI snapshots, with CSV/PDF downloads.

// Template grid is fetched from /api/v1/reports/templates at mount — that
// list is the same one the backend job dispatches on, so adding a builder
// shows up in the UI without a corresponding frontend constant edit.

// 발행 이력 "생성일" 표시. 서버가 주는 ISO 타임스탬프(예:
// "2026-06-09T11:50:02.859+09:00")를 그대로 노출하면 읽기 어려워서
// "YYYY-MM-DD HH:mm" 로 정리한다. 로그성 목록이라 상대시간보다 절대 날짜+시각이
// 읽기 좋다. 미발행(generating 등 generated_at=null)은 "—" 로 표시한다.
function formatGeneratedAt(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// 오늘 날짜를 로컬(KST) 기준 YYYY-MM-DD 로. new Date().toISOString().slice(0,10)
// 은 UTC 로 변환돼 KST 새벽(00~09시)엔 하루 전 날짜가 나오므로 로컬 컴포넌트로
// 직접 만든다(리포트 이름·period 라벨에 사용).
function todayYmd() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// 라우팅 wrapper. ReportInner 의 hook 호출 순서를 안정화하기 위해
// 로딩/권한 분기를 wrapper 에서 처리한다. 이전 구현은 manager 가 hit 하면
// /api/v1/reports 가 403 + {error, message} 를 돌려주는 걸 그대로 setReports
// 에 넣어, 다음 렌더의 reports.filter(...) 가 TypeError 를 던지고 트리 전체가
// unmount 돼 흰 화면이 됐었다.
function Report() {
  const { user, loaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded });
  if (!loaded) {
    return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;
  }
  if (!perms.canViewReports) {
    return <ReportNoAccess role={user?.role}/>;
  }
  return <ReportInner user={user} perms={perms}/>;
}

function ReportNoAccess({ role }) {
  // 매니저에겐 본인용 방문 기록 작성 화면(/m/report)을 안내한다. 그 외 권한
  // 없는 역할(guardian 등)은 단순 안내만.
  const isManager = role === "manager";
  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar active="report"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="리포트" sub="조회 권한이 없습니다"/>
          <div className="main">
            <div className="card card-pad" style={{textAlign:'center', padding:'60px 24px', maxWidth:520, margin:'40px auto'}}>
              <div style={{fontSize:42, marginBottom:14}}>🔒</div>
              <div style={{fontSize:16, fontWeight:700, marginBottom:8}}>리포트 조회 권한이 없습니다</div>
              <div className="muted" style={{fontSize:13, lineHeight:1.6, marginBottom:18}}>
                {isManager
                  ? <>매니저는 권역 리포트 분석 화면에 접근할 수 없습니다.<br/>본인이 작성한 방문 기록은 아래에서 일람할 수 있습니다.</>
                  : <>이 화면은 권역 운영자/시스템 관리자 전용입니다.<br/>접근이 필요하면 관리자에게 권한 요청 부탁드립니다.</>}
              </div>
              {isManager && (
                <a href="/m/reports" className="btn btn-primary btn-md" style={{textDecoration:'none'}}>
                  내 방문 기록 보기 →
                </a>
              )}
            </div>
          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}

function ReportInner({ user, perms }) {
  const [reports, setReports] = React.useState([]);
  const [tab, setTab] = React.useState("ready");
  const [type, setType] = React.useState("all");
  const [fromDate, setFromDate] = React.useState("");
  const [toDate,   setToDate]   = React.useState("");
  // 추가 필터(이름/템플릿/포맷) + 패널 토글.
  const [q, setQ] = React.useState("");
  const [templateFilter, setTemplateFilter] = React.useState("all");
  const [formatFilter, setFormatFilter] = React.useState("all");
  const [showFilters, setShowFilters] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [createForm, setCreateForm] = React.useState({ name: "", kind: "임시", period: "", format: "CSV" });
  const [createErr, setCreateErr] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [analytics, setAnalytics] = React.useState(null);
  const [templates, setTemplates] = React.useState([]);
  const [templateBusy, setTemplateBusy] = React.useState(null);
  const [showTemplates, setShowTemplates] = React.useState(false);
  const [preview, setPreview] = React.useState(null);    // { row, data, loading, error }
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());

  // IDs that can actually be bundled (excludes seed rows missing files).
  const selectableRows  = reports.filter(r => r.downloadable);
  const selectableIds   = React.useMemo(() => new Set(selectableRows.map(r => r.raw_id)), [reports]);
  const visibleSelected = selectableRows.filter(r => selectedIds.has(r.raw_id));
  const allSelected     = selectableRows.length > 0 && visibleSelected.length === selectableRows.length;
  const someSelected    = visibleSelected.length > 0 && !allSelected;

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) selectableRows.forEach(r => next.delete(r.raw_id));
      else             selectableRows.forEach(r => next.add(r.raw_id));
      return next;
    });
  };
  const clearSelection  = () => setSelectedIds(new Set());

  // 선택된 ID 중 실제 다운로드 가능한 것만 전송 — 화면 필터로 보이지 않는 행이라도
  // 선택 상태가 유지되니, 사용자가 의도한 ID를 그대로 보내고 검증은 백엔드에 위임.
  const bulkDownload = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    window.location.href = `/api/v1/reports/bulk_download?ids=${ids.join(",")}`;
  };

  const openPreview = async (row) => {
    setPreview({ row, data: null, loading: true, error: null });
    try {
      const res = await fetch(`/api/v1/reports/${row.raw_id}/preview`, { credentials: "same-origin" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || `${res.status} 미리보기를 불러올 수 없습니다.`);
      setPreview({ row, data: body, loading: false, error: null });
    } catch (e) {
      setPreview({ row, data: null, loading: false, error: String(e.message || e) });
    }
  };
  const closePreview = () => setPreview(null);

  const loadTemplates = React.useCallback(() =>
    fetch("/api/v1/reports/templates", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : [])
      .then(setTemplates)
      .catch(() => setTemplates([])), []);

  React.useEffect(() => {
    fetch("/api/v1/reports/analytics", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : null)
      .then(setAnalytics)
      .catch(() => setAnalytics(null));
    loadTemplates();
  }, [loadTemplates]);

  // One-click template publish. POSTs immediately with the template's
  // default name/kind, then resets the tab so the new "generating" row is
  // visible (the polling loop in another effect flips it to "ready").
  const publishTemplate = async (t) => {
    if (templateBusy) return;
    setTemplateBusy(t.key);
    try {
      const today = todayYmd();
      const res = await fetch("/api/v1/reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          report: {
            name: `${t.default_name} · ${today}`,
            kind: t.default_kind,
            period: today,
            template_key: t.key,
          },
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `${res.status} 리포트 생성 실패`);
      }
      setTab("all");
      await loadReports();
    } catch (e) {
      // No modal context here — surface as a brief alert. Users with no
      // create capability never see the click handler because the card is
      // disabled, so this should be rare.
      alert(String(e.message || e));
    } finally {
      setTemplateBusy(null);
    }
  };

  const loadReports = React.useCallback(() => {
    const params = new URLSearchParams();
    if (tab  !== "all") params.set("status", tab);
    if (type !== "all") params.set("kind", type);
    if (fromDate)       params.set("from", fromDate);
    if (toDate)         params.set("to",   toDate);
    if (q.trim())                  params.set("q", q.trim());
    if (templateFilter !== "all")  params.set("template_key", templateFilter);
    if (formatFilter   !== "all")  params.set("format", formatFilter);
    return fetch(`/api/v1/reports?${params.toString()}`).then(r => r.json()).then(setReports);
  }, [tab, type, fromDate, toDate, q, templateFilter, formatFilter]);

  // 활성화된 추가 필터 개수(버튼 배지용).
  const extraFilterCount = (q.trim() ? 1 : 0) + (templateFilter !== "all" ? 1 : 0) + (formatFilter !== "all" ? 1 : 0);
  const clearExtraFilters = () => { setQ(""); setTemplateFilter("all"); setFormatFilter("all"); };

  React.useEffect(() => { loadReports(); }, [loadReports]);

  // Poll while any row is still generating, so the badge flips without a
  // manual refresh. Stops as soon as nothing is in flight.
  const anyGenerating = reports.some(r => r.status === "generating");
  React.useEffect(() => {
    if (!anyGenerating) return;
    const t = setInterval(loadReports, 3000);
    return () => clearInterval(t);
  }, [anyGenerating, loadReports]);

  const openCreate  = () => {
    const today = todayYmd();
    setCreateForm({ name: `임시 리포트 · ${today}`, kind: "임시", period: today, format: "CSV" });
    setCreateErr(null);
    setCreating(true);
  };
  const closeCreate = () => { if (!submitting) setCreating(false); };

  const submitCreate = async () => {
    setSubmitting(true);
    setCreateErr(null);
    try {
      const res = await fetch("/api/v1/reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ report: createForm }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `${res.status} 리포트 생성 실패`);
      }
      setCreating(false);
      // Reset filters so the new row is visible (sits in "generating" briefly).
      setTab("all");
      await loadReports();
    } catch (e) {
      setCreateErr(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = reports;

  // KPI counts derived from the live report list. Subscription count + avg
  // generation time aren't exposed by the API yet, so we surface "—" rather
  // than invent numbers.
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const monthLabel = `${now.getFullYear()}년 ${now.getMonth() + 1}월`;
  const isThisMonth = (at) => typeof at === "string" && at.startsWith(thisMonth);
  const thisMonthCount = reports.filter(r => isThisMonth(r.at)).length;
  const autoCount = reports.filter(r => r.by === "자동 생성").length;
  const autoRatio = reports.length ? Math.round((autoCount / reports.length) * 100) : 0;

  // Live series from /api/v1/reports/analytics. Empty arrays during the
  // first fetch — the charts render a flat line rather than guessing.
  const monthly        = analytics?.monthly_matches         ?? [];
  const matching       = analytics?.matching_success        ?? [];
  const distrib        = analytics?.visit_hour_distribution ?? [];
  const avgGenLabel    = analytics?.avg_generation_seconds == null
                          ? "—"
                          : `${analytics.avg_generation_seconds.toFixed(1)}초`;
  const totalReports   = analytics?.total_reports ?? reports.length;
  const analyticsReady = analytics != null;

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="region" active="report"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="리포트" sub="권역 운영 데이터를 정기/임시 리포트로 발행합니다."/>
          <div className="main">

            <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:18}}>
              <div>
                <div className="h1">권역 리포트 · {monthLabel}</div>
                <div className="muted" style={{marginTop:4, fontSize:13}}>월간 자동 발행 · 주간 케어매니저 단위 발행 · 임시 발행 가능</div>
              </div>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                <div style={{display:'flex', alignItems:'center', gap:6, padding:'4px 10px', border:'1px solid var(--border)', borderRadius:8, background:'var(--surface)'}}
                     title="발행일 기준 기간 필터 (generated_at, 없으면 created_at)">
                  <span style={{color:'var(--ink-3)'}}>{Ic.filter}</span>
                  <input type="date" className="input" value={fromDate} max={toDate || undefined}
                         onChange={e => setFromDate(e.target.value)}
                         style={{border:'none', padding:'4px 2px', fontSize:12, background:'transparent', width:130}}/>
                  <span className="muted" style={{fontSize:11}}>~</span>
                  <input type="date" className="input" value={toDate} min={fromDate || undefined}
                         onChange={e => setToDate(e.target.value)}
                         style={{border:'none', padding:'4px 2px', fontSize:12, background:'transparent', width:130}}/>
                  {(fromDate || toDate) && (
                    <button onClick={() => { setFromDate(""); setToDate(""); }}
                            title="기간 초기화"
                            style={{border:'none', background:'transparent', color:'var(--ink-3)', cursor:'pointer', padding:'0 2px', display:'flex'}}>
                      {Ic.close}
                    </button>
                  )}
                </div>
                {/* 선택 0건이면 버튼이 비활성이라 클릭해도 무반응 → 왜 안 되는지 보이도록
                    인라인 안내를 띄우고, 버튼도 흐리게/not-allowed 로 명확히 비활성 표시한다. */}
                {selectedIds.size === 0 && (
                  <span className="muted" data-bulk-hint style={{fontSize:11.5, marginRight:2}}>
                    체크박스로 리포트를 선택하세요
                  </span>
                )}
                <button className="btn btn-ghost btn-md"
                  onClick={bulkDownload}
                  disabled={selectedIds.size === 0}
                  style={selectedIds.size === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  title={selectedIds.size === 0
                    ? "행 좌측 체크박스로 다운로드할 리포트를 선택하세요"
                    : `선택한 ${selectedIds.size}개 리포트를 zip으로 받기`}>
                  {Ic.arrow} 일괄 다운로드{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                </button>
                {perms.canCreateReports && (
                  <button className="btn btn-primary btn-md" onClick={openCreate}>{Ic.plus} 임시 리포트 생성</button>
                )}
              </div>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:18}}>
              <Kpi label="이달 발행 리포트" value={`${thisMonthCount}건`}/>
              <Kpi label="자동 발행 비율"   value={`${autoRatio}%`} color="var(--green-500)"/>
              <Kpi label="평균 생성 시간"   value={avgGenLabel} color="var(--sky-500)"/>
              <Kpi label="전체 리포트"      value={`${totalReports}건`} color="var(--orange-500)"/>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1.5fr 1fr', gap:14, marginBottom:14}}>
              <div className="card">
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 0'}}>
                  <div>
                    <div className="h3">월별 매칭 대상 추이</div>
                    <div className="muted" style={{fontSize:11, marginTop:2}}>
                      최근 12개월 {analyticsReady ? "" : "· 불러오는 중…"}
                    </div>
                  </div>
                  <div style={{display:'flex', gap:6}}>
                    <span className="badge badge-orange">매칭 대상</span>
                    <span className="badge badge-gray">전년</span>
                  </div>
                </div>
                <div style={{padding:'12px 18px 18px'}}>
                  {monthly.length ? <LineChart data={monthly} height={180}/> : <ChartSkeleton/>}
                </div>
              </div>
              <div className="card">
                <div style={{padding:'18px 22px 0'}}>
                  <div className="h3">매칭 성공률</div>
                  <div className="muted" style={{fontSize:11, marginTop:2}}>
                    최근 12개월 · 단위 %{analyticsReady ? "" : " · 불러오는 중…"}
                  </div>
                </div>
                <div style={{padding:'12px 18px 18px'}}>
                  {matching.length ? <LineChart data={matching} height={180} color="var(--green-500)" fill="var(--green-50)"/> : <ChartSkeleton/>}
                </div>
              </div>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1fr 1.5fr', gap:14, marginBottom:14}}>
              <div className="card">
                <div style={{padding:'18px 22px 0'}}>
                  <div className="h3">방문 시간대 분포</div>
                  <div className="muted" style={{fontSize:11, marginTop:2}}>
                    08–18시 · 1시간 단위{analyticsReady ? "" : " · 불러오는 중…"}
                  </div>
                </div>
                <div style={{padding:'12px 18px 18px'}}>
                  {distrib.length && distrib.some(v => v > 0)
                    ? <BarChart data={distrib} height={180}/>
                    : <ChartSkeleton message={analyticsReady ? "표시할 방문 기록이 없습니다" : ""}/>}
                </div>
              </div>
              <div className="card">
                <div style={{padding:'18px 22px 0', display:'flex', justifyContent:'space-between', alignItems:'baseline'}}>
                  <div>
                    <div className="h3">템플릿에서 빠르게 생성</div>
                    <div className="muted" style={{fontSize:11, marginTop:2}}>주제를 고르면 기본 옵션으로 즉시 발행됩니다.</div>
                  </div>
                  {perms.canManageSettings && (
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowTemplates(true)}
                      title="템플릿 노출/기본값 관리">{Ic.settings} 템플릿 관리</button>
                  )}
                </div>
                <div style={{padding:'14px 18px 18px', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10}}>
                  {templates.length === 0 && (
                    <div className="muted" style={{gridColumn:'1 / -1', textAlign:'center', padding:'18px 0', fontSize:12}}>
                      템플릿 목록을 불러오는 중…
                    </div>
                  )}
                  {templates.filter(t => t.enabled).length === 0 && templates.length > 0 && (
                    <div className="muted" style={{gridColumn:'1 / -1', textAlign:'center', padding:'18px 0', fontSize:12}}>
                      활성화된 템플릿이 없습니다.
                    </div>
                  )}
                  {templates.filter(t => t.enabled).map(t => {
                    const busy    = templateBusy === t.key;
                    const allowed = perms.canCreateReports;
                    const disabled = !allowed || busy;
                    const title = !allowed
                      ? "리포트 생성 권한이 없습니다 (시스템 관리자에게 요청)"
                      : busy ? "발행 요청 중…"
                      : `${t.label} 즉시 발행 (기본 옵션)`;
                    return (
                      <button key={t.key} className="card card-pad"
                        onClick={() => publishTemplate(t)}
                        disabled={disabled}
                        title={title}
                        style={{
                          textAlign:'left', padding:14, borderRadius:12,
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.55 : 1,
                        }}>
                        <span style={{width:32, height:32, borderRadius:10, background:`var(--${t.color}-50)`, color:`var(--${t.color}-${t.color==='amber'?'500':'600'})`, display:'grid', placeItems:'center'}}>
                          <span style={{width:18,height:18,display:'inline-block'}}>{Ic[t.icon]}</span>
                        </span>
                        <div style={{fontWeight:700, fontSize:12.5, marginTop:10}}>
                          {t.label}{busy && " · 요청 중…"}
                        </div>
                        <div className="muted" style={{fontSize:10.5, marginTop:2}}>{t.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="card" style={{overflow:'hidden'}}>
              <div style={{padding:'16px 22px', display:'flex', alignItems:'center', gap:14, borderBottom:'1px solid var(--border-soft)'}}>
                <div className="h3" style={{marginRight:'auto'}}>발행 이력</div>
                <div style={{display:'flex', gap:0, border:'1px solid var(--border)', borderRadius:8, overflow:'hidden'}}>
                  {[
                    {k:'ready', l:'완료'},
                    {k:'generating', l:'생성중'},
                    {k:'all', l:'전체'},
                  ].map((s,i)=>(
                    <button key={s.k} onClick={()=>setTab(s.k)} className="btn btn-ghost btn-sm"
                      style={{border:'none', borderRight:i<2?'1px solid var(--border)':'none', borderRadius:0,
                        background: tab===s.k?'var(--orange-50)':'transparent', color: tab===s.k?'var(--orange-700)':'var(--ink-2)'}}>
                      {s.l}
                    </button>
                  ))}
                </div>
                <select className="select" style={{width:120}} value={type} onChange={e=>setType(e.target.value)}>
                  <option value="all">전체 유형</option>
                  <option value="월간">월간</option>
                  <option value="주간">주간</option>
                  <option value="분기">분기</option>
                  <option value="일일">일일</option>
                  <option value="임시">임시</option>
                </select>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowFilters(v => !v)}
                  title="이름·템플릿·포맷 추가 필터"
                  style={{ background: (showFilters || extraFilterCount) ? 'var(--orange-50)' : undefined,
                           color: (showFilters || extraFilterCount) ? 'var(--orange-700)' : undefined }}>
                  {Ic.filter} 필터{extraFilterCount > 0 && (
                    <span style={{marginLeft:6, padding:'0 6px', borderRadius:99, background:'var(--orange-500)', color:'#fff', fontSize:10, fontWeight:700}}>{extraFilterCount}</span>
                  )}
                </button>
              </div>
              {showFilters && (
                <div style={{padding:'12px 22px', borderBottom:'1px solid var(--border-soft)', background:'var(--bg-sub)', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap'}}>
                  <div style={{display:'flex', alignItems:'center', gap:6}}>
                    <span style={{fontSize:11.5, fontWeight:600, color:'var(--ink-3)'}}>이름</span>
                    <input className="input" value={q} onChange={e => setQ(e.target.value)}
                      placeholder="리포트 이름 검색" style={{width:200, fontSize:12.5}}/>
                  </div>
                  <div style={{display:'flex', alignItems:'center', gap:6}}>
                    <span style={{fontSize:11.5, fontWeight:600, color:'var(--ink-3)'}}>템플릿</span>
                    <select className="select" style={{width:160, fontSize:12.5}} value={templateFilter} onChange={e => setTemplateFilter(e.target.value)}>
                      <option value="all">전체 템플릿</option>
                      {templates.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                  </div>
                  <div style={{display:'flex', alignItems:'center', gap:6}}>
                    <span style={{fontSize:11.5, fontWeight:600, color:'var(--ink-3)'}}>포맷</span>
                    <select className="select" style={{width:100, fontSize:12.5}} value={formatFilter} onChange={e => setFormatFilter(e.target.value)}>
                      <option value="all">전체</option>
                      <option value="CSV">CSV</option>
                      <option value="PDF">PDF</option>
                    </select>
                  </div>
                  {extraFilterCount > 0 && (
                    <button className="btn btn-ghost btn-sm" onClick={clearExtraFilters} style={{marginLeft:'auto'}}>필터 초기화</button>
                  )}
                </div>
              )}
              <table className="tbl">
                <thead><tr>
                  <th style={{paddingLeft:22, width:36}}>
                    <input type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected; }}
                      disabled={selectableRows.length === 0}
                      onChange={toggleAllVisible}
                      title={allSelected ? "전체 선택 해제" : "보이는 다운로드 가능 행 전체 선택"}
                      style={{cursor: selectableRows.length === 0 ? 'not-allowed' : 'pointer', accentColor:'var(--orange-500)'}}/>
                  </th>
                  <th>ID</th>
                  <th>리포트명</th>
                  <th>유형</th>
                  <th>대상 기간</th>
                  <th>형식</th>
                  <th>크기</th>
                  <th>생성일</th>
                  <th>생성자</th>
                  <th style={{textAlign:'right', paddingRight:22}}>작업</th>
                </tr></thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id}>
                      <td style={{paddingLeft:22, width:36}}>
                        <input type="checkbox"
                          checked={selectedIds.has(r.raw_id)}
                          disabled={!r.downloadable}
                          onChange={() => toggleRow(r.raw_id)}
                          title={r.downloadable ? "이 리포트 선택" : "첨부 파일이 없어 일괄 다운로드에 포함할 수 없습니다"}
                          style={{cursor: r.downloadable ? 'pointer' : 'not-allowed', accentColor:'var(--orange-500)'}}/>
                      </td>
                      <td className="mono muted">{r.id}</td>
                      <td><b>{r.name}</b></td>
                      <td><span className={"badge " + (r.type==='월간'?'badge-orange':r.type==='주간'?'badge-green':'badge-sky')}>{r.type}</span></td>
                      <td className="mono muted">{r.period}</td>
                      <td><span className="badge badge-gray">{r.fmt}</span></td>
                      <td className="mono muted">{r.size}</td>
                      <td className="mono muted" title={r.at || undefined}>{formatGeneratedAt(r.at)}</td>
                      <td>{r.by}</td>
                      <td style={{textAlign:'right', paddingRight:22}}>
                        {r.status === 'ready' && (
                          r.downloadable ? (
                            <>
                              {r.fmt === 'CSV' ? (
                                <button className="btn btn-ghost btn-sm" onClick={() => openPreview(r)}
                                        title="첫 30행을 모달로 미리 봅니다">미리보기</button>
                              ) : (
                                <button className="btn btn-ghost btn-sm" disabled
                                        title="PDF는 미리보기를 지원하지 않습니다. 다운로드하여 확인해주세요.">미리보기</button>
                              )}
                              <a className="btn btn-primary btn-sm" style={{marginLeft:6, textDecoration:'none'}}
                                 href={`/api/v1/reports/${r.raw_id}/download`}>다운로드</a>
                            </>
                          ) : (
                            // 첨부 파일이 없는 ready 행(수동 등록/시드 항목). 과거에는 미리보기·
                            // 다운로드 버튼을 disabled 로 띄워 클릭해도 무반응이라 혼란을 줬다.
                            // 동작하지 않는 버튼 대신 상태를 명확히 알리는 뱃지로 대체한다.
                            <span className="badge badge-gray"
                                  title="첨부 파일이 없는 항목입니다. 미리보기·다운로드를 제공하지 않습니다.">첨부 없음</span>
                          )
                        )}
                        {r.status === 'generating' && (
                          <span className="badge badge-amber">생성중 …</span>
                        )}
                        {r.status === 'failed' && (
                          <span className="badge badge-rose" title="생성 작업이 실패했습니다. 운영자에게 문의하세요.">실패</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={10} style={{padding:'40px 22px', textAlign:'center'}} className="muted">조건에 해당하는 리포트가 없습니다.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>

      <Modal
        open={creating}
        title="임시 리포트 생성"
        onClose={closeCreate}
        width={460}
        footer={<>
          <button className="btn btn-ghost btn-md" onClick={closeCreate} disabled={submitting}>취소</button>
          <button className="btn btn-primary btn-md" onClick={submitCreate} disabled={submitting || !createForm.name?.trim()}>
            {submitting ? "요청 중…" : "발행 요청"}
          </button>
        </>}
      >
        {createErr && (
          <div style={{background:'var(--rose-50)', color:'var(--rose-500)', padding:'8px 12px', borderRadius:8, marginBottom:12, fontSize:12}}>
            {createErr}
          </div>
        )}
        <div className="muted" style={{fontSize:11.5, marginBottom:12}}>
          백그라운드에서 CSV로 생성됩니다. 화면이 자동 갱신되며, 완료되면 "다운로드" 버튼이 활성화됩니다.
        </div>
        <div style={{display:'grid', gap:12}}>
          <Field label="리포트 이름">
            <input className="input" value={createForm.name}
                   onChange={e => setCreateForm({ ...createForm, name: e.target.value })}/>
          </Field>
          <Field label="유형">
            <select className="select" value={createForm.kind}
                    onChange={e => setCreateForm({ ...createForm, kind: e.target.value })}>
              <option value="임시">임시</option>
              <option value="일일">일일</option>
              <option value="주간">주간</option>
              <option value="월간">월간</option>
              <option value="분기">분기</option>
            </select>
          </Field>
          <Field label="대상 기간">
            <input className="input" placeholder="예: 2026-06 / W23 / 2026-Q2" value={createForm.period}
                   onChange={e => setCreateForm({ ...createForm, period: e.target.value })}/>
          </Field>
          <Field label="파일 형식">
            <div style={{display:'flex', gap:14, alignItems:'center'}}>
              {["CSV", "PDF"].map(fmt => (
                <label key={fmt} style={{display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13}}>
                  <input type="radio" name="report-format" value={fmt}
                         checked={createForm.format === fmt}
                         onChange={() => setCreateForm({ ...createForm, format: fmt })}
                         style={{accentColor:'var(--orange-500)', cursor:'pointer'}}/>
                  {fmt}
                </label>
              ))}
              <span className="muted" style={{fontSize:11, marginLeft:6}}>
                CSV는 스프레드시트로, PDF는 인쇄/배포에 적합합니다.
              </span>
            </div>
          </Field>
        </div>
      </Modal>

      <PreviewModal preview={preview} onClose={closePreview}/>
      <TemplateManageModal
        open={showTemplates}
        templates={templates}
        onClose={() => setShowTemplates(false)}
        onSaved={loadTemplates}/>
    </div>
  );
}

// 리포트 템플릿 관리 — 코드 REGISTRY 위에 얹는 오버라이드 편집.
// 신규 템플릿 생성은 빌더(서버 코드)가 필요하므로 불가하고, 기존 템플릿의
// 노출 여부(enabled)와 기본 이름/종류만 조정한다. 저장은 PATCH per template.
const REPORT_KINDS = ["월간", "주간", "분기", "일일", "임시"];

function TemplateManageModal({ open, templates, onClose, onSaved }) {
  const [draft, setDraft] = React.useState({});   // key → { enabled, default_name, default_kind }
  const [savingKey, setSavingKey] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [savedKey, setSavedKey] = React.useState(null);

  // 모달이 열릴 때 현재 effective 값으로 draft 초기화.
  React.useEffect(() => {
    if (!open) return;
    const d = {};
    templates.forEach(t => { d[t.key] = { enabled: !!t.enabled, default_name: t.default_name || "", default_kind: t.default_kind || "임시" }; });
    setDraft(d);
    setErr(null); setSavedKey(null);
  }, [open, templates]);

  const setField = (key, field) => (v) =>
    setDraft(d => ({ ...d, [key]: { ...d[key], [field]: v } }));

  const saveOne = async (key) => {
    if (savingKey) return;
    setSavingKey(key); setErr(null); setSavedKey(null);
    try {
      const res = await fetch(`/api/v1/reports/templates/${key}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(draft[key]),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || `저장 실패 (HTTP ${res.status})`);
      setSavedKey(key);
      await onSaved();
      setTimeout(() => setSavedKey(null), 2000);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Modal open={open} title="리포트 템플릿 관리" onClose={onClose} width={620}
      footer={<button className="btn btn-ghost btn-md" onClick={onClose}>닫기</button>}>
      <div className="muted" style={{fontSize:12, marginBottom:14}}>
        템플릿 카드의 <b>노출 여부</b>와 빠른 생성 시 쓰이는 <b>기본 이름·종류</b>를 조정합니다.
        신규 템플릿 추가는 서버 빌더가 필요해 지원하지 않습니다.
      </div>
      {err && <div role="alert" style={{marginBottom:12, padding:'10px 12px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}
      <div style={{display:'flex', flexDirection:'column', gap:10}}>
        {templates.map(t => {
          const d = draft[t.key] || { enabled: true, default_name: "", default_kind: "임시" };
          const busy = savingKey === t.key;
          return (
            <div key={t.key} className="card card-pad" style={{padding:14, opacity: d.enabled ? 1 : 0.6}}>
              <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:10}}>
                <span style={{width:28, height:28, borderRadius:8, background:`var(--${t.color}-50)`, color:`var(--${t.color}-600)`, display:'grid', placeItems:'center'}}>
                  <span style={{width:16,height:16,display:'inline-block'}}>{Ic[t.icon]}</span>
                </span>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontWeight:700, fontSize:13}}>{t.label}{t.schedule ? <span className="muted" style={{fontWeight:500, fontSize:11, marginLeft:6}}>· 자동 {t.schedule === 'monthly' ? '월간' : '주간'}</span> : null}</div>
                  <div className="muted" style={{fontSize:11}}>{t.desc}</div>
                </div>
                <label style={{display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer'}}>
                  <input type="checkbox" checked={d.enabled} onChange={e => setField(t.key,'enabled')(e.target.checked)}
                    style={{accentColor:'var(--orange-500)', cursor:'pointer'}}/>
                  노출
                </label>
              </div>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                <input className="input" style={{flex:1}} value={d.default_name} placeholder="기본 리포트 이름"
                  onChange={e => setField(t.key,'default_name')(e.target.value)}/>
                <select className="select" style={{width:90}} value={d.default_kind}
                  onChange={e => setField(t.key,'default_kind')(e.target.value)}>
                  {REPORT_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
                <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => saveOne(t.key)}
                  style={{minWidth:64}}>
                  {busy ? "저장…" : savedKey === t.key ? "✓ 저장" : "저장"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function PreviewModal({ preview, onClose }) {
  if (!preview) return null;
  const { row, data, loading, error } = preview;
  const downloadHref = `/api/v1/reports/${row.raw_id}/download`;

  return (
    <Modal
      open={true}
      title={`미리보기 · ${row.name}`}
      onClose={onClose}
      width={860}
      footer={<>
        <button className="btn btn-ghost btn-md" onClick={onClose}>닫기</button>
        <a className="btn btn-primary btn-md" href={downloadHref} style={{textDecoration:'none'}}>
          파일 다운로드
        </a>
      </>}
    >
      {loading && <div className="muted" style={{padding:'30px 0', textAlign:'center'}}>불러오는 중…</div>}
      {error && (
        <div style={{background:'var(--rose-50)', color:'var(--rose-500)', padding:'10px 14px', borderRadius:8, fontSize:12.5}}>
          {error}
        </div>
      )}
      {!loading && !error && data && (
        <>
          {data.metadata?.length > 0 && (
            <div style={{display:'grid', gridTemplateColumns:'120px 1fr', rowGap:6, columnGap:14, marginBottom:14, fontSize:12.5}}>
              {data.metadata.map((kv, i) => (
                <React.Fragment key={i}>
                  <div className="muted" style={{fontWeight:600}}>{kv[0]}</div>
                  <div style={{fontWeight:600}}>{kv[1]}</div>
                </React.Fragment>
              ))}
            </div>
          )}

          <div className="muted" style={{fontSize:11.5, marginBottom:6}}>
            데이터 {data.rows.length}/{data.total_data_rows} 행
            {data.truncated && " · 일부만 표시됩니다. 전체는 다운로드로 받아주세요."}
          </div>

          <div style={{maxHeight:420, overflow:'auto', border:'1px solid var(--border-soft)', borderRadius:8}}>
            <table className="tbl" style={{margin:0}}>
              <thead style={{position:'sticky', top:0, background:'var(--surface-1)'}}>
                <tr>
                  {data.columns.map((c, i) => (
                    <th key={i} style={{paddingLeft: i === 0 ? 14 : undefined, fontSize:11.5, whiteSpace:'nowrap'}}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan={data.columns.length || 1} className="muted" style={{padding:'30px 0', textAlign:'center'}}>
                    데이터 행이 없습니다.
                  </td></tr>
                )}
                {data.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{paddingLeft: ci === 0 ? 14 : undefined, fontSize:12, whiteSpace:'nowrap'}}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

function ChartSkeleton({ message = "" }) {
  return (
    <div style={{
      height: 180,
      display: 'grid', placeItems: 'center',
      background: 'var(--bg-sub)',
      borderRadius: 8,
      color: 'var(--ink-3)',
      fontSize: 12,
    }}>
      {message || "차트 데이터를 불러오는 중…"}
    </div>
  );
}

export { Report };
